import { useMeasure } from 'react-use'
import classNames from 'classnames'

import Typography from '@/components/base/Typography'

import { Section } from '@/types'

type ScheduleProps = Section &
  Partial<{
    steps: {
      title: string
      overline: string
      paragraph: string
    }[]
  }>

const Schedule = ({ steps }: ScheduleProps) => {
  const [last, { height: lastHeight }] = useMeasure<HTMLDivElement>()

  return (
    <section>
      <div className="flex w-full gap-x-gutter">
        <div className="relative z-10 flex w-24 shrink-0">
          <span
            className={classNames(
              'bg-blue-soft absolute bottom-0 left-1/2 top-[4rem] block w-[.4rem] -translate-x-1/2',
              'before:absolute before:-top-[4rem] before:h-[4rem] before:w-full',
              'before:from-blue-soft before:bg-gradient-to-t before:to-transparent',
              'hidden md:block'
            )}
            style={{ bottom: lastHeight / 2 }}
          />
        </div>
        <div className="flex-grow">
          <div className="relative flex flex-col space-y-[1rem]">
            {steps?.map(({ overline, title, paragraph }, index) => {
              const isLast = index === steps.length - 1
              return (
                <div
                  ref={isLast ? last : null}
                  key={`step-${index}`}
                  className="relative"
                >
                  <span
                    className={classNames(
                      'bg-blue-soft absolute -left-12 top-1/2 size-[3rem] -translate-x-[calc(50%_+_var(--gutter))] -translate-y-1/2 rounded-full',
                      'hidden md:block'
                    )}
                  />
                  <div
                    className={classNames(
                      'w-full rounded-xxs p-sm shadow-3xl',
                      {
                        'bg-white': !isLast,
                        'bg-blue-soft': isLast
                      }
                    )}
                  >
                    <div className="grid gap-y-xs">
                      {overline ? (
                        <Typography
                          variant="caption"
                          className={classNames('uppercase', {
                            'text-blue-soft': !isLast,
                            'text-white': isLast
                          })}
                        >
                          {overline}
                        </Typography>
                      ) : null}

                      {title ? (
                        <Typography
                          variant="h6"
                          component="h6"
                          className={classNames({
                            '[&_em]:text-blue-soft text-blue': !isLast,
                            'text-white': isLast
                          })}
                        >
                          {title}
                        </Typography>
                      ) : null}

                      {paragraph ? (
                        <Typography
                          variant="body"
                          paragraph
                          className={classNames({
                            'text-neutral-20': !isLast,
                            'text-white': isLast
                          })}
                        >
                          {paragraph}
                        </Typography>
                      ) : null}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Schedule
